import { Button, DatePicker, InlineStack, IndexFilters, IndexFiltersProps, Link, useSetIndexFiltersMode, Text, Select } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import helpers from '../../helpers';
import { useDeleteUserMeta, useGetUserMeta, useUpdateUserMeta } from 'queries/user_meta.query';
import useDidMountEffect from 'components/useDidMountEffect';
import __ from 'languages/index';
import { formatDateRange } from 'components/filter.helper';
import QuickSearchUser from 'components/quickSearchUser';
import QuickSearchVehicle from 'components/quickSearchVehicle';
import { TypedUser } from 'interface/user.model';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

export default function DriverSettlementFilter({ onCallback, loading }: { onCallback: (output: any) => void; loading: boolean }) {
  const { mutateAsync: getEntity, isPending: _loading } = useGetUserMeta();
  const { mutateAsync: updateEntity, isPending: updating } = useUpdateUserMeta();
  const { mutateAsync: deleteEntity } = useDeleteUserMeta();

  const key_for_saving_setting = `tab_driver_settlement_index_`;

  /**
   * Load data
   */

  const getItem = useCallback(async (meta_key: string) => {
    meta_key = helpers.parseMetaKey(meta_key);
    return await getEntity(meta_key);
  }, []);

  const removeItem = useCallback(async (meta_key: string) => {
    meta_key = helpers.parseMetaKey(meta_key);
    return await deleteEntity(meta_key);
  }, []);

  const updateItem = useCallback(async (meta_key: string, meta_value: any) => {
    meta_key = helpers.parseMetaKey(meta_key);
    return await updateEntity({
      meta_key: meta_key,
      meta_value: meta_value,
    });
  }, []);

  /**
   * Load data
   */

  const rootSearchPath = window.location.search;
  const mainQuery: any = useMemo(() => helpers.ExtractUrl(rootSearchPath) || {}, [rootSearchPath]);

  const { query, sort, driver_id, vehicle_id, createdAt, trip_departure_date, money_collected_at, driver_settlement_status } = mainQuery;

  const [sortSelected, setSortSelected] = useState<string[]>([helpers.colonToOldSort(sort) || 'createdAt, desc']);
  const [queryValue, setQueryValue] = useState(query || '');
  const [driverID, setDriverID] = useState(driver_id || '');
  const [driverName, setDriverName] = useState(null);
  const [vehicleID, setVehicleID] = useState(vehicle_id || '');
  const [vehicleName, setVehicleName] = useState(null);
  const [driverSettlementStatus, setDriverSettlementStatus] = useState(driver_settlement_status || '');
  const [selected, setSelected] = useState(0); // tab selected
  const [dateRanges, setDateRanges] = useState<any>(formatDateRange(createdAt));
  const [tripDepartureDateRanges, setTripDepartureDateRanges] = useState<any>(formatDateRange(trip_departure_date));
  const [moneyCollectedAtRanges, setMoneyCollectedAtRanges] = useState<any>(formatDateRange(money_collected_at));
  /**
   * Change tab, load new data from url down to filter
   */
  const reloadDataDownFromURL = (__: any) => {
    const { query, createdAt, trip_departure_date, money_collected_at, driver_settlement_status, driver_id, vehicle_id } = __;

    setDriverID(driver_id || '');
    setVehicleID(vehicle_id || '');
    setQueryValue(query || '');
    setDriverSettlementStatus(driver_settlement_status || '');
    setDateRanges(formatDateRange(createdAt));
    setTripDepartureDateRanges(formatDateRange(trip_departure_date));
    setMoneyCollectedAtRanges(formatDateRange(money_collected_at));
  };

  /** Load mặc định phát đầu tiên từ URL */
  useEffect(() => {
    reloadDataDownFromURL(mainQuery);
  }, [mainQuery]);

  /***************************************************************************
/***************************************************************************
/***************************************************************************
 * FILTERs
 */

  function getCurrentSetting(): string {
    const rootSearchPath = window.location.search;
    let mainQuery: any = helpers.ExtractUrl(rootSearchPath) || false;
    return JSON.stringify(mainQuery);
  }

  /**
   * reduce loading,
   */

  const onChangeMainQueryCallback = useMemo(() => {
    return helpers.debounce((_value) => {
      onCallback?.call(this, _value);
    }, 200);
  }, []);

  const onChangeQueryCallback = useMemo(() => {
    return helpers.debounce((_value) => {
      onCallback?.call(this, _value);
    }, 500);
  }, []);

  useDidMountEffect(() => {
    if (!sortSelected) return;
    let sortString = '';
    if (Array.isArray(sortSelected)) {
      sortString = sortSelected.join('');
    } else {
      sortString = sortSelected;
    }
    onChangeMainQueryCallback({ sort: helpers.oldSortToColon(sortString) });
  }, [sortSelected]);

  /**
   * Hold array of all tab
   */
  const [itemStrings, setItemStrings] = useState(['ALL']);

  useEffect(() => {
    getItem('customer_tabs')
      .then((response) => {
        if (helpers.isEmpty(response)) return;
        setItemStrings(response || []);
      })
      .catch((e) => console.log(e, 'e_8d'));
  }, []);

  // save name to
  useDidMountEffect(() => {
    let tabsToString = JSON.stringify(itemStrings);
    updateItem('customer_tabs', tabsToString);
  }, [itemStrings]);

  /**
   * click change tab ...
   * @param index
   * @returns
   */

  const changeTabFilterAction = useCallback(async (index: number, name: string) => {
    if (index === 0) {
      reloadDataDownFromURL({});
      return onChangeMainQueryCallback(false);
    }
    onChangeMainQueryCallback(false);
    await helpers.sleep(100);
    getItem(key_for_saving_setting + name)
      .then((response: any) => {
        if (!response) return;
        onChangeMainQueryCallback(response);
        reloadDataDownFromURL(response);
      })
      .catch((_) => {});
  }, []);

  const deleteView = (index: number, value: string) => {
    const newItemStrings = [...itemStrings];
    newItemStrings.splice(index, 1);
    setItemStrings(newItemStrings);
    setSelected(0);
    removeItem(key_for_saving_setting + value);
  };

  const duplicateView = async (name: string) => {
    setItemStrings([...itemStrings, name]);
    setSelected(itemStrings.length);
    // clone setting
    updateItem(key_for_saving_setting + name, getCurrentSetting());
    return true;
  };

  const tabs: any[] =
    Array.isArray(itemStrings) &&
    itemStrings?.map((item, index) => ({
      content: item,
      index,
      onAction: () => {
        changeTabFilterAction(index, item);
      },
      id: `${item}-${index}`,
      isLocked: index === 0,
      actions:
        index === 0
          ? []
          : [
              {
                type: 'rename',
                onAction: () => {},
                onPrimaryAction: async (value: string): Promise<boolean> => {
                  const newItemsStrings = tabs?.map((item, idx) => {
                    if (idx === index) {
                      return value;
                    }
                    return item.content;
                  });
                  setItemStrings(newItemsStrings);
                  return true;
                },
              },
              {
                type: 'duplicate',
                onPrimaryAction: async (value: string): Promise<boolean> => {
                  duplicateView(value);
                  return true;
                },
              },
              {
                type: 'delete',
                onPrimaryAction: async (value: string) => {
                  deleteView(index, value);
                  return true;
                },
              },
            ],
    }));

  const { mode, setMode } = useSetIndexFiltersMode();
  const handleFiltersQueryChange = useCallback((value: string) => {
    setQueryValue(value);
    onChangeQueryCallback({ query: value, page: 1 });
  }, []);

  /**
   * Xoa hoac huy form search ...
   */
  const handleQueryValueRemove = useCallback(() => {
    setQueryValue('');
    onChangeQueryCallback({ query: '', page: 1 });
  }, []);

  const sortOptions: any[] = [
    { label: 'Ngày tạo', value: 'createdAt, asc', directionLabel: __('ascending') },
    { label: 'Ngày tạo', value: 'createdAt, desc', directionLabel: __('descending') },
    { label: 'Ngày đi', value: 'trip_departure_date, asc', directionLabel: __('ascending') },
    { label: 'Ngày đi', value: 'trip_departure_date, desc', directionLabel: __('descending') },
    { label: 'Ngày thu tiền', value: 'money_collected_at, asc', directionLabel: __('ascending') },
    { label: 'Ngày thu tiền', value: 'money_collected_at, desc', directionLabel: __('descending') },
    { label: 'Số tiền', value: 'amount, asc', directionLabel: __('ascending') },
    { label: 'Số tiền', value: 'amount, desc', directionLabel: __('descending') },
  ];

  function disambiguateLabel(key: string, value: string | any): string {
    switch (key) {
      case 'keyword':
        return `Từ khóa: ${value}`;
      case 'driver':
        return `Lái xe: ${driverName || 'Tất cả'}`;
      case 'vehicle':
        return `Xe: ${vehicleName || 'Tất cả'}`;
      case 'driver_settlement_status':
        return `Tình trạng quyết toán: ${value === '' ? 'TẤT CẢ' : value === 'OPEN' ? 'ĐANG MỞ' : 'ĐÃ ĐÓNG'}`;

      case 'daterange':
        let from = value[0] ? dayjs(Number(value[0])).tz('Asia/Ho_Chi_Minh').format('DD-MM-YYYY') : '-';
        let to = value[1] ? dayjs(Number(value[1])).tz('Asia/Ho_Chi_Minh').format('DD-MM-YYYY') : '-';
        return `Thời gian tạo: từ ${from} đến: ${to}`;
      case 'trip_departure_date':
        let tripDepartureFrom = value[0] ? dayjs(Number(value[0])).tz('Asia/Ho_Chi_Minh').format('DD-MM-YYYY') : '-';
        let tripDepartureTo = value[1] ? dayjs(Number(value[1])).tz('Asia/Ho_Chi_Minh').format('DD-MM-YYYY') : '-';
        return `Ngày đi: từ ${tripDepartureFrom} đến: ${tripDepartureTo}`;
      case 'money_collected_at':
        let moneyCollectedAtFrom = value[0] ? dayjs(Number(value[0])).tz('Asia/Ho_Chi_Minh').format('DD-MM-YYYY') : '-';
        let moneyCollectedAtTo = value[1] ? dayjs(Number(value[1])).tz('Asia/Ho_Chi_Minh').format('DD-MM-YYYY') : '-';
        return `Ngày thu tiền: từ ${moneyCollectedAtFrom} đến: ${moneyCollectedAtTo}`;
      default:
        return value as string;
    }
  }

  const handleDriverSettlementStatusWithChange = useCallback((value: string) => {
    setDriverSettlementStatus(value);
    onChangeMainQueryCallback({ driver_settlement_status: value, page: 1 });
  }, []);

  const handleDriverSettlementStatusWithRemove = useCallback(() => {
    setDriverSettlementStatus('');
    onChangeMainQueryCallback({ driver_settlement_status: '', page: 1 });
  }, []);

  const handleDriverWithChange = useCallback((value: string) => {
    setDriverID(value);
    onChangeMainQueryCallback({ driver_id: value, page: 1 });
  }, []);

  const handleDriverWithRemove = useCallback(() => {
    setDriverID('');
    setDriverName(null);
    onChangeMainQueryCallback({ driver_id: '', page: 1 });
  }, []);

  const handleVehicleWithChange = useCallback((value: string) => {
    setVehicleID(value);
    onChangeMainQueryCallback({ vehicle_id: value, page: 1 });
  }, []);

  const handleVehicleWithRemove = useCallback(() => {
    setVehicleID('');
    setVehicleName(null);
    onChangeMainQueryCallback({ vehicle_id: '', page: 1 });
  }, []);

  const handCalendarRemove = useCallback(() => {
    setDateRanges([]);
    onChangeMainQueryCallback({ createdAt: '', page: 1 });
  }, []);

  const handleTripDepartureDateRemove = useCallback(() => {
    setTripDepartureDateRanges([]);
    onChangeMainQueryCallback({ trip_departure_date: '', page: 1 });
  }, []);

  const handleMoneyCollectedAtRemove = useCallback(() => {
    setMoneyCollectedAtRanges([]);
    onChangeMainQueryCallback({ money_collected_at: '', page: 1 });
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    handleQueryValueRemove();
    handCalendarRemove();
    handleTripDepartureDateRemove();
    handleMoneyCollectedAtRemove();
    handleDriverWithRemove();
    handleVehicleWithRemove();
    handleDriverSettlementStatusWithRemove();

    /** Phải để cái này ở cuối để nó reset hết toàn bộ bộ lọc về vị trí ban đầu */
    onChangeMainQueryCallback(false);
  }, [
    handleQueryValueRemove,
    handCalendarRemove,
    handleTripDepartureDateRemove,
    handleMoneyCollectedAtRemove,
    handleDriverWithRemove,
    handleVehicleWithRemove,
    handleDriverSettlementStatusWithRemove,
    onChangeMainQueryCallback,
  ]);

  /**
   * Date time filling ...
   */

  // date range
  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const [{ startDate, endDate }, setDateRange] = useState({
    startDate: new Date().getTime(),
    endDate: new Date().getTime(),
  });

  const [{ month: tripDepartureMonth, year: tripDepartureYear }, setTripDepartureDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const [{ startDate: tripDepartureStartDate, endDate: tripDepartureEndDate }, setTripDepartureDateRange] = useState({
    startDate: new Date().getTime(),
    endDate: new Date().getTime(),
  });

  const [{ month: moneyCollectedAtMonth, year: moneyCollectedAtYear }, setMoneyCollectedAtDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const [{ startDate: moneyCollectedAtStartDate, endDate: moneyCollectedAtEndDate }, setMoneyCollectedAtDateRange] = useState({
    startDate: new Date().getTime(),
    endDate: new Date().getTime(),
  });

  function handleMonthChange(month: number, year: number): any {
    setDate({ month, year });
  }

  function handleCalendarChange({ start, end }): void {
    const normalizedStartDate = new Date(start);
    normalizedStartDate.setHours(0, 0, 0, 0);

    const normalizedEndDate = new Date(end || start);
    normalizedEndDate.setHours(23, 59, 59, 999);

    const startDate = normalizedStartDate.valueOf();
    const endDate = normalizedEndDate.valueOf();

    setDateRange({ startDate: start.valueOf(), endDate: end.valueOf() }); // for calendar
    setDateRanges([normalizedStartDate.valueOf(), normalizedEndDate.valueOf()]); // for saving ...
    onChangeMainQueryCallback({ createdAt: `gte:${startDate},lte:${endDate}`, page: 1 });
  }

  function handleTripDepartureMonthChange(month: number, year: number): any {
    setTripDepartureDate({ month, year });
  }

  function handleTripDepartureCalendarChange({ start, end }): void {
    const normalizedStartDate = new Date(start);
    normalizedStartDate.setHours(0, 0, 0, 0);

    const normalizedEndDate = new Date(end || start);
    normalizedEndDate.setHours(23, 59, 59, 999);

    const startDate = normalizedStartDate.valueOf();
    const endDate = normalizedEndDate.valueOf();

    setTripDepartureDateRange({ startDate: start.valueOf(), endDate: end.valueOf() }); // for calendar
    setTripDepartureDateRanges([normalizedStartDate.valueOf(), normalizedEndDate.valueOf()]); // for saving ...
    onChangeMainQueryCallback({ trip_departure_date: `gte:${startDate},lte:${endDate}`, page: 1 });
  }

  function handleMoneyCollectedAtMonthChange(month: number, year: number): any {
    setMoneyCollectedAtDate({ month, year });
  }

  function handleMoneyCollectedAtCalendarChange({ start, end }): void {
    const normalizedStartDate = new Date(start);
    normalizedStartDate.setHours(0, 0, 0, 0);

    const normalizedEndDate = new Date(end || start);
    normalizedEndDate.setHours(23, 59, 59, 999);

    const startDate = normalizedStartDate.valueOf();
    const endDate = normalizedEndDate.valueOf();

    setMoneyCollectedAtDateRange({ startDate: start.valueOf(), endDate: end.valueOf() }); // for calendar
    setMoneyCollectedAtRanges([normalizedStartDate.valueOf(), normalizedEndDate.valueOf()]); // for saving ...
    onChangeMainQueryCallback({ money_collected_at: `gte:${startDate},lte:${endDate}`, page: 1 });
  }

  const filters = [
    {
      key: 'driver',
      label: 'Lái xe',
      filter: (
        <QuickSearchUser
          title={''}
          noWrap
          placeholder="Tìm theo tên lái xe"
          user_role="driver"
          onClose={(u: TypedUser) => {
            handleDriverWithChange(u.user_id);
            setDriverName(u.display_name);
          }}
        />
      ),
      shortcut: true,
    },
    {
      key: 'vehicle',
      label: 'Xe',
      filter: (
        <QuickSearchVehicle
          title={''}
          placeholder="Tìm theo biển số xe"
          onSelect={(vehicle) => {
            if (!vehicle) return;
            handleVehicleWithChange(String(vehicle.id));
            setVehicleName(vehicle.license_plate);
          }}
        />
      ),
      shortcut: true,
    },

    {
      key: 'driver_settlement_status',
      label: 'Tình trạng quyết toán',
      filter: (
        <Select
          value={driverSettlementStatus}
          label="Tình trạng quyết toán"
          labelInline
          onChange={handleDriverSettlementStatusWithChange}
          options={[
            { label: 'TẤT CẢ', value: '' },
            { label: 'ĐANG MỞ', value: 'OPEN' },
            { label: 'ĐÃ ĐÓNG', value: 'CLOSED' },
          ]}
        />
      ),
      shortcut: true,
    },
    {
      key: 'daterange',
      label: 'Thời gian tạo',
      filter: (
        <>
          <DatePicker
            month={month}
            year={year}
            selected={{
              start: new Date(startDate),
              end: new Date(endDate),
            }}
            onMonthChange={handleMonthChange}
            onChange={handleCalendarChange}
            multiMonth={false}
            allowRange
          />
        </>
      ),
      shortcut: true,
    },
    {
      key: 'trip_departure_date',
      label: 'Ngày đi',
      filter: (
        <>
          <DatePicker
            month={tripDepartureMonth}
            year={tripDepartureYear}
            selected={{
              start: new Date(tripDepartureStartDate),
              end: new Date(tripDepartureEndDate),
            }}
            onMonthChange={handleTripDepartureMonthChange}
            onChange={handleTripDepartureCalendarChange}
            multiMonth={false}
            allowRange
          />
        </>
      ),
      shortcut: true,
    },
    {
      key: 'money_collected_at',
      label: 'Ngày thu tiền',
      filter: (
        <>
          <DatePicker
            month={moneyCollectedAtMonth}
            year={moneyCollectedAtYear}
            selected={{
              start: new Date(moneyCollectedAtStartDate),
              end: new Date(moneyCollectedAtEndDate),
            }}
            onMonthChange={handleMoneyCollectedAtMonthChange}
            onChange={handleMoneyCollectedAtCalendarChange}
            multiMonth={false}
            allowRange
          />
        </>
      ),
      shortcut: true,
    },
  ];

  const appliedFilters: IndexFiltersProps['appliedFilters'] = [];

  // if (!helpers.isEmpty(userRole)) {
  appliedFilters.push({
    key: 'driver',
    label: disambiguateLabel('driver', driverID),
    onRemove: handleDriverWithRemove,
  });
  appliedFilters.push({
    key: 'vehicle',
    label: disambiguateLabel('vehicle', vehicleID),
    onRemove: handleVehicleWithRemove,
  });
  appliedFilters.push({
    key: 'driver_settlement_status',
    label: disambiguateLabel('driver_settlement_status', driverSettlementStatus),
    onRemove: handleDriverSettlementStatusWithRemove,
  });
  // }

  // if (!helpers.isEmpty(dateRanges)) {
  // const key = 'daterange';
  appliedFilters.push({
    key: 'daterange',
    label: disambiguateLabel('daterange', dateRanges),
    onRemove: handCalendarRemove,
  });
  appliedFilters.push({
    key: 'trip_departure_date',
    label: disambiguateLabel('trip_departure_date', tripDepartureDateRanges),
    onRemove: handleTripDepartureDateRemove,
  });
  appliedFilters.push({
    key: 'money_collected_at',
    label: disambiguateLabel('money_collected_at', moneyCollectedAtRanges),
    onRemove: handleMoneyCollectedAtRemove,
  });
  // }

  /**
   * Lưu lại ...
   * @param value
   * @returns
   */
  const onCreateNewView = async (value: string) => {
    setItemStrings([...itemStrings, value]);
    setSelected(itemStrings.length);
    updateItem(key_for_saving_setting + value, getCurrentSetting());
    return true;
  };
  const onHandleSave = useCallback(async () => {
    if (!itemStrings[selected]) return;
    updateItem(key_for_saving_setting + itemStrings[selected], getCurrentSetting());
    return true;
  }, [selected, itemStrings]);

  const primaryActionForFilterFields: IndexFiltersProps['primaryAction'] =
    selected === 0
      ? {
          type: 'save-as',
          onAction: onCreateNewView,
          disabled: false,
          loading: false,
        }
      : {
          type: 'save',
          onAction: onHandleSave,
          disabled: false,
          loading: false,
        };

  /***************************************************************************
  /***************************************************************************
  /***************************************************************************
   * FILTERs
   */

  return (
    <>
      <IndexFilters
        loading={loading || _loading || updating}
        sortOptions={sortOptions}
        sortSelected={sortSelected}
        queryValue={queryValue}
        queryPlaceholder="Tìm kiếm"
        onQueryChange={handleFiltersQueryChange}
        onQueryClear={handleQueryValueRemove}
        onSort={setSortSelected}
        primaryAction={primaryActionForFilterFields}
        cancelAction={{
          onAction: () => {},
          disabled: false,
          loading: false,
        }}
        tabs={tabs}
        selected={selected}
        onSelect={setSelected}
        canCreateNewView={true}
        onCreateNewView={onCreateNewView}
        onClearAll={handleFiltersClearAll}
        mode={mode}
        setMode={setMode}
        filters={filters}
        appliedFilters={appliedFilters}
      />
      {mode === 'DEFAULT' ? (
        <div className="Polaris-Filters__FiltersWrapper Polaris-Filters__FiltersWrapperWithAddButton filterWrapper">
          <div className="Polaris-Filters__FiltersInner">
            {!helpers.isEmpty(queryValue) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('keyword', queryValue)}
              </span>
            ) : null}

            {!helpers.isEmpty(driverID) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('driver', driverID)}
              </span>
            ) : null}

            {!helpers.isEmpty(vehicleID) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('vehicle', vehicleID)}
              </span>
            ) : null}

            {!helpers.isEmpty(driverSettlementStatus) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('driver_settlement_status', driverSettlementStatus)}
              </span>
            ) : null}

            {!helpers.isEmpty(dateRanges) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('daterange', dateRanges)}
              </span>
            ) : null}

            {!helpers.isEmpty(tripDepartureDateRanges) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('trip_departure_date', tripDepartureDateRanges)}
              </span>
            ) : null}

            {!helpers.isEmpty(moneyCollectedAtRanges) ? (
              <span onClick={() => setMode('FILTERING' as any)} className="FilterPill">
                {disambiguateLabel('money_collected_at', moneyCollectedAtRanges)}
              </span>
            ) : null}

            {helpers.isEmpty(queryValue) &&
            helpers.isEmpty(driverID) &&
            helpers.isEmpty(vehicleID) &&
            helpers.isEmpty(driverSettlementStatus) &&
            helpers.isEmpty(dateRanges) &&
            helpers.isEmpty(tripDepartureDateRanges) &&
            helpers.isEmpty(moneyCollectedAtRanges) ? (
              <InlineStack blockAlign="center" align="start" wrap={false}>
                <Link removeUnderline onClick={() => setMode('FILTERING' as any)}>
                  {'   '}{' '}
                  <Text as="span" variant="bodySm" tone="subdued">
                    Mở bộ lọc (hoặc bấm nút F)
                  </Text>
                </Link>
              </InlineStack>
            ) : (
              <Button variant="plain" size="micro" onClick={handleFiltersClearAll}>
                xóa hết
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
